import type { PaymentCheckout } from '@repo/api-contracts';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { env, PAYMENT_SUCCESS_PATH, paymentFailPath } from '@/shared/config';
import { formatKrw } from '@/shared/lib';
import { loadPaymentWidgets, type PaymentWidgets } from '@/shared/payment-widget';
import { Button, EyebrowLabel } from '@/shared/ui';
import { describePaymentRequestError } from '../model/payment-error-message';
import { usePreparePayment } from '../model/use-prepare-payment';

type PayableCheckout = Extract<PaymentCheckout, { payable: true }>;

const PAYMENT_METHODS_ID = 'payment-methods';
const AGREEMENT_ID = 'payment-agreement';
/** 위젯 로드·그리기 실패 — SDK 원문(영어 기술 문구)은 고객에게 보이지 않고 늘 이 문구를 쓴다. */
const WIDGET_LOAD_FAILED_MESSAGE = '결제 화면을 불러오지 못했습니다. 잠시 뒤 다시 시도해 주세요.';

interface PaymentWidgetPanelProps {
  readonly checkout: PayableCheckout;
  /** 결제 준비 금액이 화면 금액과 다를 때 — 페이지가 결제 정보를 다시 부른다. */
  readonly onAmountChanged: () => void;
}

/**
 * 주문서형 결제 UI 와 결제 버튼(결제 설계 §4-2).
 * 상품 상세·주문하기와 같은 두 칸(lg): 왼쪽은 토스 결제 수단·약관 자리, 오른쪽은 주문 요약과 따라 내려오는(sticky) 결제 버튼.
 * 좁은 화면에서는 주문 요약 → 결제 수단 → 안내·버튼 순으로 쌓인다. 토스가 그리기 전에는 결제 수단·약관 자리 아래에
 * 회색 칸을 둔다(그려지는 결제 UI 가 회색 칸에 밀려 내려오지 않게).
 * 위젯을 불러오지 못하면 그 자리에 우리 문구와 다시 시도를 두고, 다시 시도하면 초점을 결제 수단 제목으로 옮긴다
 * (누른 버튼이 사라지므로). SDK 원문은 콘솔에만 남긴다. 결제창 요청 실패 문구는
 * describePaymentRequestError 가 고른다 — 사용자가 결제창을 닫았으면 안내하지 않는다.
 */
export const PaymentWidgetPanel = ({
  checkout,
  onAmountChanged,
}: PaymentWidgetPanelProps): ReactElement => {
  const [widgets, setWidgets] = useState<PaymentWidgets | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  // 다시 시도할 때마다 올린다 — 로드 effect 를 다시 돌리고, 결제 수단·약관 자리를 새 빈 칸으로 바꾼다(key).
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isRequesting, setIsRequesting] = useState(false);
  const { mutateAsync: prepare } = usePreparePayment();
  // StrictMode 이중 마운트에서 같은 자리에 위젯을 두 번 그리지 않게 고객 키·시도마다 한 번만 불러온다.
  const loadedFor = useRef<string | null>(null);
  const methodsHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const clientKey = env.VITE_TOSS_CLIENT_KEY;

    if (!clientKey) {
      setError('결제 설정이 없어 결제를 진행할 수 없습니다.');
      return;
    }

    const loadKey = `${checkout.customerKey}#${loadAttempt}`;

    if (loadedFor.current === loadKey) {
      return;
    }

    loadedFor.current = loadKey;

    loadPaymentWidgets(clientKey, checkout.customerKey)
      .then(async (loaded) => {
        // 토스는 금액을 먼저 넣어야 결제 UI 를 그린다.
        await loaded.setAmount(checkout.amount);
        await loaded.render({
          paymentMethods: `#${PAYMENT_METHODS_ID}`,
          agreement: `#${AGREEMENT_ID}`,
        });
        setWidgets(loaded);
      })
      .catch((loadError: unknown) => {
        // SDK 원문(예: [TossPayments SDK] Failed to load script)은 개발자용이라 콘솔에만 남긴다.
        console.error(loadError);
        setIsLoadFailed(true);
      });
  }, [checkout.customerKey, checkout.amount, loadAttempt]);

  // 결제 정보를 다시 불러와 금액이 바뀌면 위젯 금액도 맞춘다.
  useEffect(() => {
    if (widgets) {
      void widgets.setAmount(checkout.amount);
    }
  }, [widgets, checkout.amount]);

  const handleRetryLoad = (): void => {
    methodsHeadingRef.current?.focus();
    setIsLoadFailed(false);
    setLoadAttempt((attempt) => attempt + 1);
  };

  const handlePay = async (): Promise<void> => {
    if (!widgets) {
      return;
    }

    setError(null);
    setIsRequesting(true);

    try {
      const prepared = await prepare({ orderId: checkout.orderId });

      if (prepared.amount !== checkout.amount) {
        setError('금액이 바뀌었습니다. 바뀐 금액을 확인한 뒤 다시 결제해 주세요.');
        onAmountChanged();
        return;
      }

      await widgets.requestPayment({
        orderId: prepared.paymentId,
        orderName: prepared.orderName,
        successUrl: `${window.location.origin}${PAYMENT_SUCCESS_PATH}`,
        failUrl: `${window.location.origin}${paymentFailPath(checkout.orderId)}`,
      });
    } catch (payError) {
      const { message, shouldLog } = describePaymentRequestError(payError);

      if (shouldLog) {
        console.error(payError);
      }

      setError(message);
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <section
      aria-label="결제"
      className="grid gap-10 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:grid-rows-[auto_1fr] lg:gap-x-12 lg:gap-y-8 xl:gap-x-16"
    >
      <div className="flex flex-col gap-4 lg:col-start-2 lg:row-start-1 lg:border-t lg:border-foreground lg:pt-5">
        <div className="flex flex-col gap-1.5">
          <EyebrowLabel>Order</EyebrowLabel>
          <h2 className="text-sm font-semibold">주문 요약</h2>
        </div>
        <dl className="divide-y divide-foreground/10 border-y border-foreground/10 text-sm wrap-anywhere">
          <div className="flex gap-4 py-3">
            <dt className="w-24 shrink-0 text-muted-foreground sm:w-28">주문</dt>
            <dd className="min-w-0 flex-1 font-medium">{checkout.orderName}</dd>
          </div>
        </dl>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <p className="text-sm font-semibold">결제 금액</p>
          <p className="text-[2rem] leading-none font-bold tracking-[-0.02em] tabular-nums">
            {formatKrw(checkout.amount)}
          </p>
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-6 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        <div className="flex flex-col gap-3 border-b border-foreground pb-4">
          <EyebrowLabel>Payment Method</EyebrowLabel>
          <h2
            ref={methodsHeadingRef}
            tabIndex={-1}
            className="text-2xl leading-tight font-bold tracking-[-0.02em] outline-none"
          >
            결제 수단
          </h2>
        </div>
        {/* 토스 자리 묶음은 사이 간격(gap)이 없다 — 빈 자리(그리기 전)가 간격을 차지하지 않아 자리표시가 제목 바로 아래 오고,
            약관은 그려진 뒤에만 위 간격(not-empty:mt-6)을 둔다. 시도마다 새 칸(key) — 앞 시도가 그리다 만 토스 UI 위에 겹쳐 그리지 않는다. */}
        <div className="flex flex-col">
          <div key={`methods-${loadAttempt}`} id={PAYMENT_METHODS_ID} />
          <div key={`agreement-${loadAttempt}`} id={AGREEMENT_ID} className="not-empty:mt-6" />
          {widgets === null && error === null && !isLoadFailed ? (
            <div aria-hidden className="h-64 w-full animate-pulse bg-muted/70" />
          ) : null}
          {isLoadFailed ? (
            // 결제 수단 제목의 굵은 아랫선 바로 아래라 칸 선을 따로 두지 않는다.
            <div
              role="alert"
              className="flex flex-col items-center gap-4 py-12 text-center sm:py-16"
            >
              <p className="text-sm text-balance text-destructive">{WIDGET_LOAD_FAILED_MESSAGE}</p>
              <Button
                type="button"
                variant="outline"
                className="h-11 border-foreground/20 px-6"
                onClick={handleRetryLoad}
              >
                다시 시도
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:sticky lg:top-24 lg:col-start-2 lg:row-start-2 lg:self-start">
        {error ? (
          <p
            role="alert"
            className="border-l-2 border-destructive bg-destructive/5 px-5 py-4 text-sm text-destructive"
          >
            {error}
          </p>
        ) : null}
        <Button
          type="button"
          size="lg"
          className="h-14 w-full text-base"
          disabled={!widgets || isRequesting}
          isLoading={isRequesting}
          onClick={() => {
            void handlePay();
          }}
        >
          {formatKrw(checkout.amount)} 결제하기
        </Button>
      </div>
    </section>
  );
};
